import {
  AgingMeasure,
  AgingTicket,
  TicketsView,
} from '@totem/components/ticketing/agingDashboard/types';

export const PRIORITIES = ['P1', 'P2', 'P3', 'P4'];

export const PRIORITY_LABELS: Record<string, string> = {
  P1: 'P1 Critical',
  P2: 'P2 High',
  P3: 'P3 Medium',
  P4: 'P4 Low',
};

// Priority is an ordered scale; reuse the vulnerability severity ramp (one hue, critical darkest).
export const PRIORITY_SEVERITY: Record<
  string,
  'critical' | 'high' | 'medium' | 'low'
> = {
  P1: 'critical',
  P2: 'high',
  P3: 'medium',
  P4: 'low',
};

export const PRIORITY_TAG_COLORS: Record<string, string> = {
  P1: 'red',
  P2: 'volcano',
  P3: 'orange',
  P4: 'default',
};

export const formatDays = (days: number) => {
  if (days < 0 || isNaN(days)) {
    return '-';
  }
  if (days < 1) {
    const hours = Math.round(days * 24);
    return hours < 1 ? '< 1h' : `${hours}h`;
  }
  return days < 10 ? `${days.toFixed(1)}d` : `${Math.round(days)}d`;
};

export const formatHours = (hours: number) => {
  if (hours < 0 || hours === null || isNaN(hours)) {
    return '-';
  }
  if (hours < 1) {
    return `${Math.max(1, Math.round(hours * 60))}m`;
  }
  if (hours < 48) {
    const whole = Math.floor(hours);
    const minutes = Math.round((hours - whole) * 60);
    return minutes > 0 && whole < 10
      ? `${whole}h ${minutes}m`
      : `${Math.round(hours)}h`;
  }
  return formatDays(hours / 24);
};

export const formatThreshold = (hours: number) =>
  hours % 24 === 0
    ? `${hours / 24} day${hours === 24 ? '' : 's'}`
    : `${hours} hours`;

export const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric' })
    : '-';

export const isActive = (ticket: AgingTicket) => ticket.status !== 'RESOLVED';

// Same bucket edges as the API (since update: 1/3/7/14/30 days; since opened: 1/7/30/60 days).
const UPDATE_EDGES: [string, number][] = [
  ['lt1', 1],
  ['1to3', 3],
  ['3to7', 7],
  ['7to14', 14],
  ['14to30', 30],
  ['30plus', Infinity],
];
const OPENED_EDGES: [string, number][] = [
  ['lt24h', 1],
  ['1to7', 7],
  ['8to30', 30],
  ['31to60', 60],
  ['60plus', Infinity],
];

export const bucketOf = (ticket: AgingTicket, measure: AgingMeasure) => {
  const days = measure === 'update' ? ticket.staleDays : ticket.ageDays;
  const edges = measure === 'update' ? UPDATE_EDGES : OPENED_EDGES;
  return edges.find(([, max]) => days < max)?.[0];
};

export const inView = (ticket: AgingTicket, view: TicketsView) => {
  switch (view) {
    case 'breached':
      return isActive(ticket) && ticket.breached;
    case 'stale7':
      return isActive(ticket) && ticket.staleDays >= 7;
    case 'awaitingReply':
      return isActive(ticket) && ticket.firstReplyHours === null;
    case 'followUpOverdue':
      return isActive(ticket) && ticket.followUpOverdue;
    case 'resolved':
      return !isActive(ticket);
    default:
      return isActive(ticket);
  }
};
